import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, PROPERTY } from "../../data";

export const Navbar = ({ onRequestPackage }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-warm-border/70 shadow-[0_1px_20px_rgba(11,37,69,0.04)]" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <a href="#overview" data-testid="nav-logo" className="flex flex-col leading-none shrink-0">
          <span className={`font-serif text-xl md:text-2xl font-semibold ${scrolled ? "text-navy" : "text-white"}`}>
            Bay Quarter Shores
          </span>
          <span className={`label-caps text-[10px] mt-0.5 ${scrolled ? "text-water" : "text-white/70"}`}>
            Lot 17-L · Homesite
          </span>
        </a>

        <div className="hidden xl:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium transition-colors ${
                scrolled ? "text-warm hover:text-navy" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <span
            data-testid="nav-asking-price"
            className={`hidden md:inline text-sm font-medium ${scrolled ? "text-warm" : "text-white/80"}`}
          >
            Asking Price: <span className={scrolled ? "text-navy" : "text-white"}>{PROPERTY.price}</span>
          </span>
          <button
            data-testid="nav-request-package-btn"
            onClick={onRequestPackage}
            className="hidden sm:inline-flex bg-water text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-water-soft transition-colors shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-water focus-visible:outline-none"
          >
            Request Due Diligence Package
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`xl:hidden p-2 rounded-full transition-colors ${scrolled ? "text-navy" : "text-white"}`}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden backdrop-blur-xl bg-white/95 border-b border-warm-border overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="py-3 text-navy font-medium border-b border-warm-light last:border-0"
                >
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-request-btn"
                onClick={() => { setOpen(false); onRequestPackage(); }}
                className="mt-4 bg-water text-white rounded-full px-6 py-3 text-sm font-medium"
              >
                Request Due Diligence Package
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
